import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  Select,
  VStack,
  Text,
  InputGroup,
  InputRightElement,
  IconButton,
  Link,
  HStack,
  Spinner,
  Box,
  useColorMode,
  SimpleGrid
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash,
  faCheck,
  faExternalLinkAlt,
  faRobot,
  faInfoCircle,
  faPlay
} from '@fortawesome/free-solid-svg-icons';
import {
  AI_PROVIDERS,
  PROVIDER_ORDER,
  AUTO_MODEL,
  NO_AI,
  getProviderKey,
  setProviderKey,
  getActiveProvider,
  setActiveProvider,
  shouldUseAI,
  discoverModels,
  isConfigValid
} from '../../utils/ai/aiProviders';
import { testConnection } from '../../utils/ai/aiService';
import { useThemedToast } from '../../utils/useThemedToast';

// Individual provider card
const ProviderCard = ({ providerId, isActive, storedKey, onActivate, onKeyChange, onTest, testingId, testResults, colorMode }) => {
  const provider = AI_PROVIDERS[providerId];
  const [apiKey, setApiKey] = useState(storedKey?.apiKey || '');
  const [endpoint, setEndpoint] = useState(storedKey?.endpoint || 'http://localhost:11434');
  const [model, setModel] = useState(storedKey?.model || AUTO_MODEL);
  const [showKey, setShowKey] = useState(false);
  // Live-discovered models for the optional override dropdown (seeded with fallbacks)
  const [models, setModels] = useState(provider.fallbackModels || []);

  // Sync from external state when modal opens
  useEffect(() => {
    setApiKey(storedKey?.apiKey || '');
    setEndpoint(storedKey?.endpoint || 'http://localhost:11434');
    setModel(storedKey?.model || AUTO_MODEL);
  }, [storedKey]);

  const isTesting = testingId === providerId;
  const testResult = testResults[providerId];
  const hasKey = provider.requiresKey ? !!apiKey : !!endpoint;
  const config = {
    provider: providerId,
    apiKey: provider.requiresKey ? apiKey : null,
    endpoint: provider.requiresEndpoint ? endpoint : null,
    model
  };
  const valid = isConfigValid(config);

  // Fetch the live model list for the override dropdown. Kept as a manual callback so
  // endpoint-only providers (Ollama) probe localhost ONLY on an explicit user action.
  // A localhost fetch from this public HTTPS origin triggers the browser's Private
  // Network Access prompt ("wants to access other apps on this device").
  const runDiscovery = useCallback(() => {
    if (!valid) return;
    discoverModels(providerId, apiKey, endpoint)
      .then((ids) => {
        if (ids.length) setModels(ids.map((id) => ({ id, name: id })));
      })
      .catch(() => { /* keep fallback list */ });
  }, [valid, providerId, apiKey, endpoint]);

  // Key-based providers hit public HTTPS APIs, so auto-refresh as soon as the config is
  // valid. Endpoint-only providers (Ollama) wait for an explicit opt-in (see handleActivate)
  // to avoid firing the local-network permission prompt on modal open.
  useEffect(() => {
    if (!valid || provider.requiresEndpoint) return;
    runDiscovery();
  }, [valid, provider.requiresEndpoint, runDiscovery]);

  const handleKeyBlur = () => {
    if (hasKey) {
      setProviderKey(providerId, apiKey, model, endpoint);
      onKeyChange();
    }
  };

  const handleModelChange = (e) => {
    const newModel = e.target.value;
    setModel(newModel);
    if (hasKey) {
      setProviderKey(providerId, apiKey, newModel, endpoint);
      onKeyChange();
    }
  };

  const handleActivate = () => {
    if (hasKey) {
      setProviderKey(providerId, apiKey, model, endpoint);
    }
    onActivate(providerId);
    // Endpoint-only providers probe localhost only here, in direct response to the user
    // opting in, so the browser's local-network prompt is expected rather than a surprise.
    if (provider.requiresEndpoint) runDiscovery();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleKeyBlur();
      if (!isActive && hasKey) handleActivate();
    }
  };

  const borderClr = isActive
    ? 'orange.400'
    : testResult?.success === true
      ? 'green.500'
      : testResult?.success === false
        ? 'red.500'
        : colorMode === 'dark' ? 'whiteAlpha.200' : 'gray.200';

  return (
    <Box
      p={4}
      borderWidth="2px"
      borderRadius="lg"
      borderColor={borderClr}
      bg={colorMode === 'dark' ? 'whiteAlpha.50' : 'gray.50'}
      cursor="pointer"
      onClick={handleActivate}
      transition="border-color 0.2s"
      _hover={{ borderColor: isActive ? 'orange.400' : (colorMode === 'dark' ? 'whiteAlpha.400' : 'gray.400') }}
      position="relative"
    >
      {/* Header */}
      <HStack justify="space-between" mb={3}>
        <HStack spacing={2}>
          <Text fontWeight="700" fontSize="sm">{provider.name}</Text>
          {valid && (
            <Box color="green.400" fontSize="xs">
              <FontAwesomeIcon icon={faCheck} />
            </Box>
          )}
        </HStack>
        {isActive && (
          <Text fontSize="xs" fontWeight="600" color="orange.400">ACTIVE</Text>
        )}
      </HStack>

      {/* Model selector */}
      <Box mb={2} onClick={(e) => e.stopPropagation()}>
        <Select
          size="sm"
          value={model}
          onChange={handleModelChange}
          bg={colorMode === 'dark' ? '#1a1a1a' : 'white'}
          borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
          fontSize="xs"
        >
          <option value={AUTO_MODEL}>Auto: cheapest (recommended)</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </Select>
      </Box>

      {/* API Key / Endpoint input */}
      <Box mb={2} onClick={(e) => e.stopPropagation()}>
        {provider.requiresKey ? (
          <InputGroup size="sm">
            <Input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              onBlur={handleKeyBlur}
              onKeyDown={handleKeyDown}
              placeholder={provider.keyPlaceholder}
              bg={colorMode === 'dark' ? '#1a1a1a' : 'white'}
              borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
              fontSize="xs"
              pr="2.5rem"
            />
            <InputRightElement>
              <IconButton
                size="xs"
                variant="ghost"
                aria-label={showKey ? 'Hide' : 'Show'}
                icon={<FontAwesomeIcon icon={showKey ? faEyeSlash : faEye} size="xs" />}
                onClick={() => setShowKey(!showKey)}
              />
            </InputRightElement>
          </InputGroup>
        ) : (
          <>
            <Input
              size="sm"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              onBlur={handleKeyBlur}
              onKeyDown={handleKeyDown}
              placeholder="http://localhost:11434"
              bg={colorMode === 'dark' ? '#1a1a1a' : 'white'}
              borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
              fontSize="xs"
            />
            <Text fontSize="10px" color="gray.500" mt={1}>Run: ollama serve</Text>
            <Text fontSize="10px" color="gray.500" mt={1}>
              Connects to a local Ollama instance. Your browser will ask permission to access your local network. Nothing leaves your device.
            </Text>
          </>
        )}
      </Box>

      {/* Footer: get key link + cost + test */}
      <HStack justify="space-between" align="center" onClick={(e) => e.stopPropagation()}>
        <VStack align="start" spacing={0}>
          {provider.requiresKey && (
            <Link href={provider.keyUrl} isExternal fontSize="10px" color="orange.400" _hover={{ textDecoration: 'underline' }}>
              Get key <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '8px', marginLeft: 2 }} />
            </Link>
          )}
        </VStack>

        <Button
          size="xs"
          variant="outline"
          onClick={() => onTest(config)}
          isLoading={isTesting}
          loadingText=""
          spinner={<Spinner size="xs" />}
          isDisabled={!valid || isTesting}
          colorScheme={testResult?.success === true ? 'green' : testResult?.success === false ? 'red' : 'gray'}
          borderColor={colorMode === 'dark' ? 'gray.600' : 'gray.300'}
          leftIcon={!isTesting ? <FontAwesomeIcon icon={testResult?.success ? faCheck : faPlay} /> : undefined}
        >
          {testResult?.success === true ? 'OK' : testResult?.success === false ? 'Failed' : 'Test'}
        </Button>
      </HStack>

      {/* Test error message */}
      {testResult?.success === false && (
        <Text fontSize="10px" color="red.400" mt={1} noOfLines={2}>{testResult.message}</Text>
      )}
    </Box>
  );
};

// Built-in, no-key option: our own data-driven engine.
const TcsAiCard = ({ isActive, onActivate, colorMode }) => {
  const borderClr = isActive
    ? 'orange.400'
    : colorMode === 'dark' ? 'whiteAlpha.200' : 'gray.200';

  return (
    <Box
      p={4}
      borderWidth="2px"
      borderRadius="lg"
      borderColor={borderClr}
      bg={colorMode === 'dark' ? 'whiteAlpha.50' : 'gray.50'}
      cursor="pointer"
      onClick={() => onActivate(NO_AI)}
      transition="border-color 0.2s"
      _hover={{ borderColor: isActive ? 'orange.400' : (colorMode === 'dark' ? 'whiteAlpha.400' : 'gray.400') }}
      position="relative"
    >
      <HStack justify="space-between" mb={3}>
        <HStack spacing={2}>
          {/* Same three-square + bot mark used in the app header */}
          <HStack spacing="3px">
            <Box bg="gray.500" w="16px" h="16px" borderRadius="2px" />
            <Box bg="gray.500" w="16px" h="16px" borderRadius="2px" />
            <Box bg="orange.400" w="16px" h="16px" borderRadius="2px" display="flex" alignItems="center" justifyContent="center">
              <FontAwesomeIcon icon={faRobot} color="#ffffff" style={{ fontSize: '9px' }} />
            </Box>
          </HStack>
          <Text fontWeight="700" fontSize="sm">Timechain Stats AI</Text>
        </HStack>
        {isActive && (
          <Text fontSize="xs" fontWeight="600" color="orange.400">ACTIVE</Text>
        )}
      </HStack>
      <Text fontSize="sm" fontWeight="600" color={colorMode === 'dark' ? 'green.300' : 'green.600'} mb={1}>
        Free · no API key needed
      </Text>
      <Text fontSize="sm" color={colorMode === 'dark' ? 'gray.300' : 'gray.600'} lineHeight="1.5">
        Try Timechain Stats AI, trained on thousands of live Bitcoin network datapoints. A private analysis that you can use for free.
      </Text>
    </Box>
  );
};

export const AIConfigModal = ({ isOpen, onClose, onConfigured }) => {
  const { colorMode } = useColorMode();
  const bgColor = colorMode === 'dark' ? '#121212' : '#ffffff';
  const borderColor = colorMode === 'dark' ? '#ffffff' : '#121212';
  const toast = useThemedToast();

  const [activeId, setActiveId] = useState(() => getActiveProvider());
  const [keys, setKeys] = useState(() => {
    const result = {};
    for (const id of PROVIDER_ORDER) {
      result[id] = getProviderKey(id);
    }
    return result;
  });
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState({});

  // Refresh state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveId(getActiveProvider());
      const result = {};
      for (const id of PROVIDER_ORDER) {
        result[id] = getProviderKey(id);
      }
      setKeys(result);
      setTestingId(null);
      setTestResults({});
    }
  }, [isOpen]);

  const refreshKeys = useCallback(() => {
    const result = {};
    for (const id of PROVIDER_ORDER) {
      result[id] = getProviderKey(id);
    }
    setKeys(result);
  }, []);

  const handleActivate = useCallback((providerId) => {
    setActiveProvider(providerId);
    setActiveId(providerId);
  }, []);

  const handleTest = useCallback(async (config) => {
    const id = config.provider;
    setTestingId(id);
    setTestResults(prev => ({ ...prev, [id]: undefined }));

    try {
      await testConnection(config);
      setTestResults(prev => ({ ...prev, [id]: { success: true } }));
      toast({ title: `${AI_PROVIDERS[id]?.name} connected`, status: 'success', duration: 2000 });
    } catch (err) {
      setTestResults(prev => ({ ...prev, [id]: { success: false, message: err.message } }));
      toast({ title: `${AI_PROVIDERS[id]?.name} failed`, description: err.message, status: 'error', duration: 4000 });
    } finally {
      setTestingId(null);
    }
  }, [toast]);

  const handleDone = () => {
    onConfigured?.();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '5xl' }}>
      <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.9)" />
      <ModalContent
        bg={bgColor}
        pb={4}
        borderWidth="2px"
        borderStyle="solid"
        borderColor={borderColor}
        borderRadius="6px"
        overflow="hidden"
        maxH="85vh"
      >
        <ModalHeader>
          <FontAwesomeIcon icon={faRobot} /> &nbsp;Choose AI Provider
        </ModalHeader>
        <ModalCloseButton
          _hover={{ bg: 'gray.500' }}
          _focus={{ boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)' }}
        />
        <ModalBody overflowY="auto">
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
              <TcsAiCard
                isActive={activeId === NO_AI || !shouldUseAI()}
                onActivate={handleActivate}
                colorMode={colorMode}
              />
              {PROVIDER_ORDER.map((id) => (
                <ProviderCard
                  key={id}
                  providerId={id}
                  isActive={activeId === id}
                  storedKey={keys[id]}
                  onActivate={handleActivate}
                  onKeyChange={refreshKeys}
                  onTest={handleTest}
                  testingId={testingId}
                  testResults={testResults}
                  colorMode={colorMode}
                />
              ))}
            </SimpleGrid>

            <HStack spacing={2} fontSize="sm" color={colorMode === 'dark' ? 'gray.300' : 'gray.600'} justify="center">
              <FontAwesomeIcon icon={faInfoCircle} />
              <Text>Your API keys are stored locally and never sent to our servers.</Text>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="orange" onClick={handleDone}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
